import { useMemo, useState } from 'react';
import type { RuleTemplate } from '@wave/shared';
import { Modal } from '../Modal';
import { useTelegramChats, useTelegramTemplates } from '../../api/hooks';
import { useCreateRuleFromTemplate } from '../../api/mutations';
import {
  btnPrimary,
  btnSecondary,
  cardL2,
  fieldLabelCls,
  inputCls,
  subSectionTitle,
} from '../../lib/styles';

/**
 * TemplatePicker — каталог pre-built правил, сгруппированных по категории.
 * Оператор выбирает template, привязывает к чатам и сохраняет — получает
 * полностью готовое к работе правило.
 *
 * Категории:
 *  - bank: Тинькофф/Сбер/АльфаБанк
 *  - exchange: Binance/Bybit P2P
 *  - rates: курс-боты
 *  - custom: всё остальное
 */
const CATEGORY_LABEL: Record<string, string> = {
  bank: 'Банки',
  exchange: 'Обменники',
  rates: 'Курсы',
  custom: 'Прочее',
};

export function TelegramTemplatePicker({ onClose }: { onClose: () => void }) {
  const { data: templates, isLoading } = useTelegramTemplates();
  const { data: chats } = useTelegramChats();
  const create = useCreateRuleFromTemplate();
  const [selected, setSelected] = useState<RuleTemplate | null>(null);
  const [chatIds, setChatIds] = useState<string[]>([]);
  const [customName, setCustomName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const byCategory = useMemo(() => {
    const map = new Map<string, RuleTemplate[]>();
    for (const t of templates ?? []) {
      const arr = map.get(t.category) ?? [];
      arr.push(t);
      map.set(t.category, arr);
    }
    return map;
  }, [templates]);

  async function applyTemplate() {
    if (!selected) return;
    setError(null);
    try {
      await create.mutateAsync({
        templateKey: selected.key,
        name: customName.trim() || undefined,
        chatIds,
      });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Modal title="Библиотека правил" onClose={onClose} size="lg">
      <div className="space-y-4">
        <p className="text-sm text-text2">
          Готовые правила для популярных банков, обменников и курс-ботов. Выбери — заполнятся все
          поля, тебе останется только привязать чаты.
        </p>

        {isLoading && <div className="text-sm text-text2">Загрузка…</div>}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_320px]">
          {/* Левая колонка: список templates */}
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {[...byCategory.entries()].map(([cat, tpls]) => (
              <div key={cat}>
                <h4 className={`${subSectionTitle} mb-1.5`}>{CATEGORY_LABEL[cat] ?? cat}</h4>
                <div className="space-y-1.5">
                  {tpls.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        setSelected(t);
                        setCustomName(t.name);
                      }}
                      className={`block w-full rounded-md border p-2.5 text-left text-sm transition-colors ${
                        selected?.id === t.id
                          ? 'border-[var(--accent-border)] bg-[var(--accent-bg)]'
                          : 'border-border bg-bg hover:border-border-strong'
                      }`}
                    >
                      <div className={`font-medium ${selected?.id === t.id ? 'text-accent' : 'text-text'}`}>
                        {t.name}
                      </div>
                      {t.description && (
                        <div className="mt-0.5 text-[11px] text-text-subtle">{t.description}</div>
                      )}
                      <div className="mt-1 font-mono text-[10px] text-text-subtle">
                        Пример: <span className="text-text">{t.sampleText}</span>
                      </div>
                      <div className="mt-0.5 text-[10px] text-text-subtle">
                        Действий: {t.actions.length}
                        {t.guards.length > 0 && ` · Условий: ${t.guards.length}`}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Правая колонка: настройка перед сохранением */}
          <div className={cardL2}>
            {!selected ? (
              <div className="text-sm text-text-subtle">← Выбери template слева</div>
            ) : (
              <div className="space-y-3">
                <div>
                  <h4 className={`${subSectionTitle} mb-2`}>Настройка</h4>
                  <p className="text-xs text-text-subtle">{selected.name}</p>
                </div>
                <div>
                  <label className={fieldLabelCls}>Название правила</label>
                  <input
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    className={inputCls}
                    placeholder={selected.name}
                  />
                </div>
                <div>
                  <label className={fieldLabelCls}>Чаты</label>
                  <div className="max-h-32 space-y-1 overflow-y-auto rounded-md border border-border bg-bg p-1.5">
                    {(chats ?? []).map((c) => (
                      <label
                        key={c.chatId}
                        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs text-text hover:bg-bg2"
                      >
                        <input
                          type="checkbox"
                          checked={chatIds.includes(c.chatId)}
                          onChange={(e) => {
                            if (e.target.checked) setChatIds([...chatIds, c.chatId]);
                            else setChatIds(chatIds.filter((id) => id !== c.chatId));
                          }}
                        />
                        <span>{c.chatTitle ?? c.chatId}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {error && (
                  <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-2 py-1 text-xs text-danger">
                    {error}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border-subtle pt-3">
          <button type="button" onClick={onClose} className={btnSecondary}>
            Отмена
          </button>
          <button
            type="button"
            disabled={!selected || create.isPending}
            onClick={() => void applyTemplate()}
            className={btnPrimary}
          >
            {create.isPending ? 'Создание…' : 'Создать правило'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
